import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, ChevronRight, Check, Minus, Plus } from 'lucide-react';
import { apiGet, fmt } from '../lib/api';
import { useCart } from '../context/CartContext';
import { Stars, StockBadge } from '../components/Badges';
import ProductCard from '../components/ProductCard';

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState('specs');
  const [zoom, setZoom] = useState(null);
  const imgRef = useRef(null);

  useEffect(() => {
    setP(null);
    setQty(1);
    apiGet(`/products/${slug}`).then(setP).catch(() => nav('/shop'));
  }, [slug, nav]);

  if (!p) {
    return (
      <div className="bg-cream min-h-screen pt-40 pb-24">
        <div className="max-w-5xl mx-auto px-4 animate-pulse space-y-6">
          <div className="h-8 bg-silver rounded w-1/3" />
          <div className="grid md:grid-cols-2 gap-10">
            <div className="aspect-square bg-silver rounded-2xl" />
            <div className="space-y-4">
              <div className="h-10 bg-silver rounded" />
              <div className="h-6 bg-silver rounded w-2/3" />
              <div className="h-24 bg-silver rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const onMouseMove = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    setZoom({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };

  const tabs = [
    { id: 'specs', label: 'Specifications' },
    { id: 'compat', label: 'Compatibility' },
    { id: 'reviews', label: `Reviews (${p.review_count})` },
  ];

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <nav className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wide text-sub" data-testid="breadcrumb-nav">
          <Link to="/" className="hover:text-flame">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/shop?category=${p.category_slug}`} className="hover:text-flame">{p.category_name}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-forest font-semibold truncate">{p.sku}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div
            ref={imgRef}
            onMouseMove={onMouseMove}
            onMouseLeave={() => setZoom(null)}
            className="relative rounded-3xl overflow-hidden bg-forest aspect-square border border-emerald-950/10"
            data-testid="product-gallery"
          >
            <img
              src={p.image}
              alt={p.name}
              className="w-full h-full object-cover transition-transform duration-200"
              style={
                zoom
                  ? { transform: 'scale(1.6)', transformOrigin: `${zoom.x}% ${zoom.y}%` }
                  : undefined
              }
            />
            {p.badge && (
              <span className="absolute top-4 left-4 bg-flame text-white font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full">
                {p.badge}
              </span>
            )}
            <span className="absolute bottom-4 right-4 font-mono text-[10px] uppercase tracking-widest text-cream/70 bg-forest/60 backdrop-blur px-3 py-1.5 rounded-full">
              Hover to zoom
            </span>
          </div>
        </div>

        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame">{p.category_name}</p>
          <h1 data-testid="product-detail-name" className="font-display font-extrabold uppercase tracking-tight text-forest text-4xl sm:text-5xl leading-[0.98] mt-2">
            {p.name}
          </h1>
          <div className="flex items-center gap-3 mt-4">
            <Stars rating={p.rating} />
            <span className="text-sm text-sub">{p.rating} · {p.review_count} reviews</span>
          </div>
          <div className="flex items-end gap-4 mt-6">
            <span data-testid="product-detail-price" className="font-display font-bold text-5xl text-forest">{fmt(p.price)}</span>
            {p.compare_at_price && (
              <span className="text-xl text-sub line-through mb-1.5">{fmt(p.compare_at_price)}</span>
            )}
          </div>
          <div className="mt-4">
            <StockBadge stock={p.stock} />
          </div>
          <p className="text-ink/80 leading-relaxed mt-6">{p.description}</p>

          <div className="flex flex-wrap gap-2 mt-5">
            <span className="font-mono text-[11px] uppercase tracking-wide bg-white border border-emerald-950/10 rounded-full px-3 py-1.5">SKU {p.sku}</span>
            <span className="font-mono text-[11px] uppercase tracking-wide bg-white border border-emerald-950/10 rounded-full px-3 py-1.5">OEM {p.oem_number}</span>
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-8">
            <div className="flex items-center gap-1 bg-white border border-emerald-950/15 rounded-full">
              <button data-testid="qty-decrease-button" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:text-flame" aria-label="Decrease"><Minus className="w-4 h-4" /></button>
              <span data-testid="qty-value" className="w-8 text-center font-bold">{qty}</span>
              <button data-testid="qty-increase-button" onClick={() => setQty(Math.min(99, qty + 1))} className="p-3 hover:text-flame" aria-label="Increase"><Plus className="w-4 h-4" /></button>
            </div>
            <button
              data-testid="product-add-to-cart-button"
              disabled={p.stock <= 0}
              onClick={() => add(p, qty)}
              className="flex-1 min-w-[200px] inline-flex justify-center items-center gap-2 bg-forest hover:bg-flame text-cream font-display font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-colors disabled:opacity-40"
            >
              <ShoppingCart className="w-5 h-5" /> Add to Cart
            </button>
          </div>
          <button
            data-testid="buy-now-button"
            disabled={p.stock <= 0}
            onClick={() => { add(p, qty); nav('/checkout'); }}
            className="w-full mt-3 inline-flex justify-center items-center gap-2 border-2 border-forest text-forest hover:bg-forest hover:text-cream font-display font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-colors disabled:opacity-40"
          >
            Buy Now
          </button>

          <div className="mt-8 bg-pine/10 border border-pine/20 rounded-2xl p-5">
            <p className="flex items-center gap-2 font-semibold text-forest text-sm">
              <Check className="w-4 h-4 text-ok" /> Fits these tractors & similar models
            </p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {p.fits.map((f) => (
                <span key={f} className="font-mono text-[11px] bg-white border border-emerald-950/10 rounded-full px-3 py-1">{f}</span>
              ))}
              {p.brands.map((b) => (
                <Link key={b} to={`/shop?brand=${encodeURIComponent(b)}`} className="font-mono text-[11px] bg-forest text-cream rounded-full px-3 py-1 hover:bg-flame transition">
                  {b} parts →
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="flex gap-1 border-b border-emerald-950/10">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`product-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 font-display font-semibold uppercase tracking-wide text-sm border-b-2 -mb-px transition ${
                tab === t.id ? 'border-flame text-forest' : 'border-transparent text-sub hover:text-forest'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="py-8">
          {tab === 'specs' && (
            <div data-testid="specs-table" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl">
              {Object.entries(p.specs).map(([k, v]) => (
                <div key={k} className="bg-white border border-emerald-950/10 rounded-xl p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-sub">{k}</p>
                  <p className="font-display font-bold text-lg text-forest mt-1">{v}</p>
                </div>
              ))}
            </div>
          )}
          {tab === 'compat' && (
            <div data-testid="compatibility-list" className="max-w-2xl">
              <p className="text-sm text-sub mb-4">Confirmed fitment — verify against your serial number if in doubt, or call 1-800-TRACTOR.</p>
              <div className="bg-white border border-emerald-950/10 rounded-2xl divide-y divide-emerald-950/5">
                {p.fits.map((f) => (
                  <div key={f} className="flex items-center justify-between px-5 py-3.5">
                    <span className="font-semibold text-forest">{f}</span>
                    <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase text-ok"><Check className="w-3.5 h-3.5" /> Fits</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab === 'reviews' && (
            <div data-testid="reviews-tab" className="max-w-2xl bg-white border border-emerald-950/10 rounded-2xl p-6 flex items-center gap-6">
              <div className="text-center">
                <p className="font-display font-bold text-6xl text-forest">{p.rating}</p>
                <Stars rating={p.rating} />
                <p className="text-xs text-sub mt-1">{p.review_count} verified reviews</p>
              </div>
              <p className="text-sm text-ink/70 leading-relaxed">
                Ratings come from verified purchases by farmers, repair shops and dealers across the US.
                Every review is tied to a real order number.
              </p>
            </div>
          )}
        </div>

        {p.related?.length > 0 && (
          <div>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-forest text-3xl mb-8">Pairs well with</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {p.related.map((r, i) => (
                <ProductCard key={r.id} p={r} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
